/**
 * todo - resolve typing issues in another story
 */
import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { trackEvent } from '~/utils/bvPixel';
import useAnalyticsStore from '~/stores/useAnalyticsStore';
import usePageDataStore from '~/stores/usePageData';
import useReviewsStatisticsStore from './useReviewsStatisticsStore';
import useReviewsStore from './useReviewsStore';
import { useProductStore } from '~/stores/useProductStore';

/**
 * This store manages the UI state for toggling the visibility of the main reviews section,
 * allowing it to be collapsed or expanded by the user.
 */
const useReviewsToggleStore = defineStore('reviewsToggleStore', () => {
  const { product } = storeToRefs(useProductStore());
  const { currentResults } = storeToRefs(useReviewsStore());
  const { totalCount } = storeToRefs(useReviewsStatisticsStore());
  const { pageData } = storeToRefs(usePageDataStore());
  const analyticsStore = useAnalyticsStore();

  const { showFirstPageOfReviewsTreatment = false } = pageData.value?.experimentFlags ?? {};

  // State
  const isReviewsSectionCondensed = ref<boolean>(true);

  // Getters
  /**
   * Determines if the reviews section should have the expand/collapse feature.
   * The feature is disabled if an experiment flag is active or if there are no reviews.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isCondensable = computed<boolean>(() => {
    if (showFirstPageOfReviewsTreatment || !totalCount?.value) return false;
    return !!currentResults.value;
  });

  /**
   * Represents the current collapsed/expanded state of the reviews section.
   * This is the primary value components should use to toggle their view.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isCondensed = computed<boolean>(() => {
    if (showFirstPageOfReviewsTreatment || !totalCount?.value) return false;
    return isReviewsSectionCondensed.value;
  });

  // Actions
  /** Sets the condensed/expanded state of the reviews section. */
  function setIsCondensed(value: boolean) {
    isReviewsSectionCondensed.value = value;
  }

  /**
   * Toggles the condensed state and fires analytics events to track user interaction.
   */
  function toggleIsCondensed() {
    setIsCondensed(!isReviewsSectionCondensed.value);
    const isCondensedState = isReviewsSectionCondensed.value;
    trackEvent('read_reviews', product.value?.id, isCondensedState ? 'condensed' : 'expanded');
    if (!isCondensedState && !import.meta.server) {
      analyticsStore.fireLinkTagOnce('expand-reviews');
    }
  }

  function expandReviews() {
    setIsCondensed(false);
  }

  return {
    // Getters
    isCondensed,
    isCondensable,
    // Actions
    toggleIsCondensed,
    expandReviews,
  };
});

export default useReviewsToggleStore;
